import React, { useState, useEffect } from 'react';
import { Form, Button, Typography, Upload, Card, List, Progress, Alert, Space, Tag, Grid } from 'antd';
import { 
    FileTextOutlined, 
    UploadOutlined, 
    CheckCircleOutlined, 
    ExclamationCircleOutlined,
    DeleteOutlined,
    EyeOutlined
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { useSelfRegistration } from '../../hooks/useSelfRegistration';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import { SelfRegistrationSessionDTO, RequiredDocumentDTO, UploadedDocumentDTO } from '@/shared/api/model';


const { Title, Text } = Typography;
const { Dragger } = Upload;
const { useBreakpoint } = Grid;

interface DocumentUploadStepProps {
    session: SelfRegistrationSessionDTO;
    onComplete: (session: SelfRegistrationSessionDTO) => void;
    onError: (message: string) => void;
}

const DocumentUploadStep: React.FC<DocumentUploadStepProps> = ({
    session,
    onComplete,
    onError
}) => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    
    const [requiredDocuments, setRequiredDocuments] = useState<RequiredDocumentDTO[]>([]);
    const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocumentDTO[]>([]);
    const [currentDocumentType, setCurrentDocumentType] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Use the refactored self registration hook
    const {
        requiredDocuments: hookRequiredDocuments,
        uploadDocument,
        getCurrentSession,
        isLoadingRequiredDocuments,
        isUploadingDocument,
        fetchRequiredDocuments
    } = useSelfRegistration();

    useEffect(() => {
        setError(null);
        // Use required documents from session DTO directly — already populated by the backend
        if (session.requiredDocuments && session.requiredDocuments.length > 0) {
            setRequiredDocuments(session.requiredDocuments);
        } else if (hookRequiredDocuments && Array.isArray(hookRequiredDocuments) && hookRequiredDocuments.length > 0) {
            setRequiredDocuments(hookRequiredDocuments);
        } else {
            // Fallback: fetch from API
            fetchRequiredDocuments().then((result: any) => {
                const docs = result?.data?.data ?? result?.data ?? result;
                if (Array.isArray(docs) && docs.length > 0) {
                    setRequiredDocuments(docs);
                }
            }).catch(() => {});
        }

        if (session.uploadedDocuments) {
            setUploadedDocuments(session.uploadedDocuments);
        }
    }, [session.sessionToken]);

    const isDocumentUploaded = (documentType: string): UploadedDocumentDTO | undefined => {
        return uploadedDocuments.find(doc => doc.documentType === documentType);
    };

    const getUploadProgress = (): number => {
        const requiredDocs = requiredDocuments.filter(doc => doc.required);
        const uploadedRequiredDocs = requiredDocs.filter(doc => doc.documentType && isDocumentUploaded(doc.documentType));
        return requiredDocs.length > 0 ? (uploadedRequiredDocs.length / requiredDocs.length) * 100 : 0;
    };

    const canProceed = (): boolean => {
        const requiredDocs = requiredDocuments.filter(doc => doc.required);
        return requiredDocs.every(doc => {
            const uploaded = doc.documentType ? isDocumentUploaded(doc.documentType) : undefined;
            // Allow progression if document is uploaded (regardless of validation status)
            // @ts-ignore
            return uploaded && (uploaded.status === 'VALIDATED' || uploaded.status === 'UPLOADED' || uploaded.status === 'PENDING_VALIDATION');
        });
    };

    const handleFileUpload = async (file: File, documentType: string) => {
        const document = requiredDocuments.find(doc => doc.documentType === documentType);
        if (!document) return;

        // Clear any existing errors
        setError(null);

        // Validate file size
        if (document.maxSizeBytes && file.size > document.maxSizeBytes) {
            const errorMessage = `File size exceeds maximum allowed size of ${(document.maxSizeBytes / 1048576).toFixed(1)}MB`;
            setError(errorMessage);
            onError(errorMessage);
            return;
        }

        // Validate file format (case-insensitive)
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        // @ts-ignore
        const allowedFormatsLower = document.allowedFormats?.map(format => format.toLowerCase()) || [];
        
        console.log('🔍 File extension:', fileExtension);
        console.log('🔍 Allowed formats:', allowedFormatsLower);
        
        if (!fileExtension || (allowedFormatsLower.length > 0 && !allowedFormatsLower.includes(fileExtension))) {
            const errorMessage = `File format not allowed. Allowed formats: ${document.allowedFormats?.join(', ') || 'any'}`;
            setError(errorMessage);
            onError(errorMessage);
            return;
        }

        try {
            // Call the actual API using the refactored hook with correct signature
            console.log('🔄 Uploading document:', {
                sessionToken: session.sessionToken,
                documentType: documentType,
                fileName: file.name,
                fileSize: file.size
            });
            
            if (!session.sessionToken) {
                throw new Error('Session token is required');
            }
            
            const uploadedDoc = await uploadDocument(session.sessionToken, documentType, file);
            setUploadedDocuments(prev => [...prev.filter(doc => doc.documentType !== documentType), uploadedDoc]);
            showSuccessNotification(`${documentType} uploaded successfully`);
            // Clear error on successful upload
            setError(null);
            
        } catch (error: any) {
            const errorMessage = error.message || `Failed to upload ${documentType}`;
            setError(errorMessage);
            onError(errorMessage);
        }
    };

    const handleDeleteDocument = async (documentType: string) => {
        try {
            // TODO: Implement delete document API call
            // For now, just remove from local state
            setUploadedDocuments(prev => prev.filter(doc => doc.documentType !== documentType));
            showSuccessNotification(`${documentType} removed successfully`);
        } catch (error: any) {
            onError(error.message || 'Failed to delete document');
        }
    };

    const handleContinue = async () => {
        if (!canProceed()) {
            const errorMessage = 'Please upload all required documents before continuing';
            setError(errorMessage);
            onError(errorMessage);
            return;
        }

        try {
            setError(null);
            const updatedSession = await getCurrentSession();
            if (!updatedSession) throw new Error('Session not found');
            showSuccessNotification('Documents uploaded successfully');
            onComplete(updatedSession);
        } catch (error: any) {
            const errorMessage = error.message || 'Failed to proceed to next step';
            setError(errorMessage);
            onError(errorMessage);
        }
    };

    const uploadProps: UploadProps = {
        name: 'file',
        multiple: false,
        showUploadList: false,
        beforeUpload: (file) => {
            if (currentDocumentType) {
                handleFileUpload(file, currentDocumentType);
            }
            return false; // Prevent default upload
        }
    };

    return (
        <div>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '30px' }}>
                <FileTextOutlined style={{ 
                    fontSize: isMobile ? '36px' : '48px', 
                    color: '#1890ff', 
                    marginBottom: isMobile ? '12px' : '16px' 
                }} />
                <Title level={isMobile ? 4 : 3}>Document Upload</Title>
                <Text type="secondary" style={{ 
                    fontSize: isMobile ? '14px' : '16px',
                    display: 'block'
                }}>
                    Upload the required documents to complete your registration
                </Text>
            </div>

            {/* Progress Indicator */}
            <Card 
                style={{ marginBottom: isMobile ? '16px' : '20px' }}
                size={isMobile ? 'small' : 'default'}
            >
                <div style={{ textAlign: 'center' }}>
                    <Progress
                        type="circle"
                        percent={Math.round(getUploadProgress())}
                        format={(percent) => `${percent}%`}
                        status={canProceed() ? 'success' : 'active'}
                        size={isMobile ? 80 : 120}
                    />
                    <div style={{ marginTop: '10px' }}>
                        <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Upload Progress</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                            {uploadedDocuments.filter(doc => 
                                doc.documentType && requiredDocuments.find(req => req.documentType === doc.documentType)?.required
                            ).length} of {requiredDocuments.filter(doc => doc.required).length} required documents uploaded
                        </Text>
                    </div>
                </div>
            </Card>

            {/* Document List */}
            <List
                dataSource={requiredDocuments}
                size={isMobile ? 'small' : 'default'}
                renderItem={(document) => {
                    // @ts-ignore
                    const uploadedDoc = isDocumentUploaded(document.documentType);
                    const isUploaded = !!uploadedDoc;
                    
                    return (
                        <List.Item style={{ padding: isMobile ? '8px 0' : '12px 0' }}>
                            <Card
                                style={{ 
                                    width: '100%',
                                    border: isUploaded ? '1px solid #52c41a' : '1px solid #d9d9d9'
                                }}
                                bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
                                size={isMobile ? 'small' : 'default'}
                            >
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: isMobile ? 'flex-start' : 'flex-start',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    gap: isMobile ? '12px' : '0'
                                }}>
                                    <div style={{ flex: 1, width: isMobile ? '100%' : 'auto' }}>
                                        <Space 
                                            direction={isMobile ? 'vertical' : 'horizontal'} 
                                            size={isMobile ? 'small' : 'middle'}
                                            style={{ width: '100%' }}
                                        >
                                            <div>
                                                <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
                                                    {document.displayName}
                                                </Text>
                                            </div>
                                            <div>
                                                {document.required && <Tag color="red"
                                                    // @ts-ignore
                                                     size={isMobile ? 'small' : 'default'}>Required</Tag>}
                                                {!document.required && <Tag color="blue"
                                                    // @ts-ignore
                                                       size={isMobile ? 'small' : 'default'}>Optional</Tag>}
                                                {isUploaded && <Tag color="green" icon={<CheckCircleOutlined />}
                                                    // @ts-ignore
                                                     size={isMobile ? 'small' : 'default'}>Uploaded</Tag>}
                                            </div>
                                        </Space>
                                        <div style={{ marginTop: isMobile ? '6px' : '8px' }}>
                                            <Text type="secondary" style={{ fontSize: isMobile ? '13px' : '14px' }}>
                                                {document.description}
                                            </Text>
                                        </div>
                                        <div style={{ marginTop: '4px' }}>
                                            <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px' }}>
                                                Max size: {document.maxSizeBytes ? (document.maxSizeBytes / 1048576).toFixed(1) : 'N/A'}MB | 
                                                Formats: {document.allowedFormats?.join(', ') || 'Any'}
                                            </Text>
                                        </div>
                                        {uploadedDoc && (
                                            <div style={{ marginTop: isMobile ? '6px' : '8px' }}>
                                                <Text style={{ fontSize: isMobile ? '11px' : '12px' }}>
                                                    File: {uploadedDoc.originalFileName || 'Unknown'} ({uploadedDoc.fileSize ? (uploadedDoc.fileSize / 1024).toFixed(1) : 'Unknown'}KB)
                                                </Text>
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ 
                                        marginLeft: isMobile ? '0' : '16px',
                                        width: isMobile ? '100%' : 'auto',
                                        display: 'flex',
                                        justifyContent: isMobile ? 'center' : 'flex-end'
                                    }}>
                                        {!isUploaded ? (
                                            <Upload {...uploadProps}>
                                                <Button
                                                    type="primary"
                                                    icon={<UploadOutlined />}
                                                    onClick={() => setCurrentDocumentType(document.documentType || '')}
                                                    loading={isUploadingDocument && currentDocumentType === document.documentType}
                                                    // @ts-ignore
                                                    size={isMobile ? 'middle' : 'default'}
                                                    style={{ 
                                                        width: isMobile ? '100%' : 'auto',
                                                        height: isMobile ? '40px' : '32px'
                                                    }}
                                                >
                                                    Upload
                                                </Button>
                                            </Upload>
                                        ) : (
                                            <Space 
                                                direction={isMobile ? 'horizontal' : 'horizontal'}
                                                size={isMobile ? 'small' : 'middle'}
                                                style={{ width: isMobile ? '100%' : 'auto' }}
                                            >
                                                <Button
                                                    type="text"
                                                    icon={<EyeOutlined />}
                                                    size={isMobile ? 'small' : 'small'}
                                                    style={{ 
                                                        fontSize: isMobile ? '12px' : '14px',
                                                        flex: isMobile ? 1 : 'none'
                                                    }}
                                                >
                                                    View
                                                </Button>
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    size={isMobile ? 'small' : 'small'}
                                                    onClick={() => document.documentType && handleDeleteDocument(document.documentType)}
                                                    style={{ 
                                                        fontSize: isMobile ? '12px' : '14px',
                                                        flex: isMobile ? 1 : 'none'
                                                    }}
                                                >
                                                    Delete
                                                </Button>
                                            </Space>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        </List.Item>
                    );
                }}
            />

            {/* Error Display */}
            {error && (
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setError(null)}
                    style={{ 
                        marginTop: isMobile ? '16px' : '20px', 
                        marginBottom: isMobile ? '16px' : '20px' 
                    }}
                />
            )}

            {/* Upload Instructions */}
            <Alert
                message="Upload Instructions"
                description={
                    <ul style={{ 
                        marginBottom: 0, 
                        paddingLeft: isMobile ? '16px' : '20px',
                        fontSize: isMobile ? '13px' : '14px'
                    }}>
                        <li>Ensure all documents are clear and readable</li>
                        <li>Upload documents in the specified formats only</li>
                        <li>File sizes should not exceed the maximum limit</li>
                        <li>All required documents must be uploaded to proceed</li>
                    </ul>
                }
                type="info"
                showIcon
                // @ts-ignore
                size={isMobile ? 'small' : 'default'}
                style={{ 
                    marginTop: isMobile ? '16px' : '20px', 
                    marginBottom: isMobile ? '20px' : '30px' 
                }}
            />

            {/* Continue Button */}
            <Button
                type="primary"
                size={isMobile ? 'middle' : 'large'}
                block
                onClick={handleContinue}
                loading={false}
                disabled={!canProceed()}
                style={{ 
                    height: isMobile ? '44px' : '48px',
                    fontSize: isMobile ? '16px' : '18px'
                }}
            >
                {canProceed() ? 'Continue to Next Step' : 'Upload Required Documents to Continue'}
            </Button>

            {!canProceed() && (
                <div style={{ textAlign: 'center', marginTop: '10px' }}>
                    <Text type="secondary" style={{ fontSize: isMobile ? '13px' : '14px' }}>
                        <ExclamationCircleOutlined /> Please upload all required documents before proceeding
                    </Text>
                </div>
            )}
        </div>
    );
};

export default DocumentUploadStep;
